// @ts-strict-ignore
import { ContainerType } from 'flow/store/engine/types';
import type { Action, PublicId } from 'flow/store/engine/types';
import { sortSnippetInTopologicalOrder, createSnippetVersion } from '../../snippets';
import type { SnippetVersion } from '../../types';
import { indexActionsByPublicId, isActionOfType } from 'flow/service/actions';
import { getActionsRequiringGateRule } from 'flow/service/actions/snippets';
import { indexThresholdsByPublicId } from 'flow/service/thresholds';
import { ActionType, booleanRuleStrings } from 'flow/store/engine/constants';
import { createAction } from 'flow/store/engine/create/actions/actions';
import { addThresholdToBooleanRule } from 'flow/store/engine/update/rules/rules';
import { createSnippetGateThreshold } from 'flow/store/engine/update/thresholds';
import { generatePublicId } from 'flow/service/publicIds';


export function prepareLinkedSnippetForInsertion(args: {
  snippet: SnippetVersion;
  localToRemotePublicIds: Record<PublicId, PublicId>;
  parentSnippetActionPublicId: PublicId;
}): SnippetVersion {
  const { snippet, localToRemotePublicIds, parentSnippetActionPublicId } = args;

  const snippetWithSnippetParentAction = attachParentSnippetAction({
    snippet,
    parentSnippetActionPublicId,
  });

  const snippetWithAttachedChildContainerContexts = attachSnippetChildActionContainerContext({
    localToRemotePublicIds,
    snippet: snippetWithSnippetParentAction,
  });

  const snippetWithGateRules = attachGateRules({
    snippet: snippetWithAttachedChildContainerContexts,
    parentSnippetActionPublicId,
  });

  const sortedSnippet = sortSnippetInTopologicalOrder({
    snippet: snippetWithGateRules,
  });

  return sortedSnippet;
}


export function attachParentSnippetAction(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
}) {
  const { snippet, parentSnippetActionPublicId } = args;

  if (snippet.metadata === null) {
    throw new Error('linked snippet must contain metadata');
  }

  const snippetMetadata = snippet.props.snippets.find(
    s => snippet.metadata.token === s.token,
  );

  const snippetParentAction = createAction({
    type: ActionType.SNIPPET,
    args: {
      name: snippetMetadata?.name,
      publicId: parentSnippetActionPublicId,
      publicVersion: snippet.metadata.version,
      token: snippet.metadata.token,
    },
  });


  const updatedActions = [...Object.values(snippet.data.actions), snippetParentAction];

  return createSnippetVersion({
    actions: indexActionsByPublicId(updatedActions),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    lists: snippet.props.lists,
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    services: snippet.props.services,
    tags: snippet.props.tags,
    metadata: snippet.metadata,
    snippets: snippet.props.snippets,
  });
}


function attachSnippetChildActionContainerContext(args: {
  snippet: SnippetVersion;
  localToRemotePublicIds: Record<PublicId, PublicId>;
}): SnippetVersion {
  const { snippet, localToRemotePublicIds } = args;


  const snippetActions = Object.values(snippet.data.actions).filter(snippetAction => (
    isActionOfType(snippetAction, ActionType.SNIPPET)
  ));

  const snippetActionCount = snippetActions.length;
  if (snippetActionCount !== 1) {
    throw new Error(`snippet action count (${snippetActionCount}) cannot be different than 1`);
  }

  const [snippetParentAction] = snippetActions;

  const actionsWithContainerContext: Action[] = Object.values(snippet.data.actions)
    .map(snippetAction => {
      // do not update the snippet parent action as it should not have any container context
      if (snippetAction === snippetParentAction) {
        return snippetAction;
      }

      const remoteActionPublicId = localToRemotePublicIds[snippetAction.publicId];
      if (!remoteActionPublicId) {
        throw new Error(`could not resolve remote action public id: ${snippetAction.publicId}`);
      }

      return {
        ...snippetAction,
        containerContext: {
          type: ContainerType.SNIPPET,
          actionOptionPublicId: generatePublicId('actionoption'),
          snippetRemoteActionMap: {
            /**
             * the map contains a link between the snippet parent and the id of this same
             * snippet action _at_ the workflow version related to the snippet parent
             */
            [snippetParentAction.publicId]: remoteActionPublicId,
          },
        },
      };
    });

  return createSnippetVersion({
    actions: indexActionsByPublicId(actionsWithContainerContext),
    thresholds: snippet.data.thresholds,
    dataRoutes: snippet.data.dataRoutes,
    lists: snippet.props.lists,
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    services: snippet.props.services,
    tags: snippet.props.tags,
    metadata: snippet.metadata,
  });
}


export function attachGateRules(args: {
  snippet: SnippetVersion;
  parentSnippetActionPublicId: PublicId;
}) {
  const { snippet, parentSnippetActionPublicId } = args;
  const snippetActions = snippet.data.actions;

  // Create gate rule thresholds for any actions which required them
  const actionsRequiringGateRule = getActionsRequiringGateRule(snippetActions);
  const snippetGateThresholds = actionsRequiringGateRule
    .map(snippetAction => createSnippetGateThreshold({
      parentActionId: parentSnippetActionPublicId,
      ownerActionId: snippetAction.publicId,
    }));

  // Update actions to include new gate rules
  const updatedActionsMap = { ...snippetActions };
  for (const threshold of snippetGateThresholds) {
    const action = updatedActionsMap[threshold.actionId];
    if (action) {
      const mergedRules = addThresholdToBooleanRule({
        thresholdId: threshold.publicId,
        pathToPerformActionAt: booleanRuleStrings.AND_OPERATOR_STRING,
        originalRule: action.rules ?? {},
      });
      updatedActionsMap[threshold.actionId] = { ...action, rules: mergedRules };
    }
  }

  const updatedActions = Object.values(updatedActionsMap);
  const updatedThresholds = [
    ...Object.values(snippet.data.thresholds),
    ...snippetGateThresholds,
  ];

  return createSnippetVersion({
    actions: indexActionsByPublicId(updatedActions),
    thresholds: indexThresholdsByPublicId(updatedThresholds),
    dataRoutes: snippet.data.dataRoutes,
    lists: snippet.props.lists,
    outcomes: snippet.props.outcomes,
    publishedAttributes: snippet.props.publishedAttributes,
    reasonCodeGroups: snippet.props.reasonCodeGroups,
    services: snippet.props.services,
    tags: snippet.props.tags,
    metadata: snippet.metadata,
  });
}
